import type { TestSingleChoiceElementValue } from '@vedalib/editor/lib/elements'
import {
  TestAnswerFeedbackDisplayOption,
  TestAnswerFeedbackSelectOption,
  TestAnswerFeedbackType,
} from '@vedalib/editor/lib/elements'
import React, { useMemo } from 'react'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { FieldSegmented, FieldSelect, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { Typography } from 'components/uiKit/Typography'
import { useHiddenTestFeedback } from 'services/Branding/fieldConfigs/useFieldConfigs'
import { t } from 'services/Translation'
import { getEnumOption } from 'utils/enum'
import { chainName } from 'utils/form'

import type { TestSingleChoiceValueFormType } from './testSingleChoiceTypes'

const useHiddenPerOption = ({ name }: IFieldConfig) => {
  const data = useFormData<TestSingleChoiceElementValue>(name, 1)
  const hidden = data?.displayOption === TestAnswerFeedbackDisplayOption.general
  return useMemo(() => ({ hidden }), [hidden])
}

const useHiddenGeneral = ({ name }: IFieldConfig) => {
  const data = useFormData<TestSingleChoiceElementValue>(name, 1)
  const hidden = data?.displayOption === TestAnswerFeedbackDisplayOption.perOption
  return useMemo(() => ({ hidden }), [hidden])
}

const IS_RANDOM = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'isRandom',
  label: t('input.label.shuffle'),
})

const ANSWER_FEEDBACK = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'answerFeedback',
  label: t('elements.testAnswerFeedback.form.answerFeedback'),
  defaultValue: true,
})

const DISPLAY_FEEDBACK = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'displayFeedback',
  label: t('elements.testAnswerFeedback.form.displayFeedback'),
  defaultValue: true,
})

const DISPLAY_OPTION = genElementValueField({
  as: FieldSelect<TestAnswerFeedbackDisplayOption>,
  name: 'displayOption',
  label: t('elements.testAnswerFeedback.form.displayOption'),
  defaultValue: TestAnswerFeedbackDisplayOption.perOption,
  props: {
    options: [
      getEnumOption('TestAnswerFeedbackDisplayOption', TestAnswerFeedbackDisplayOption.general),
      getEnumOption('TestAnswerFeedbackDisplayOption', TestAnswerFeedbackDisplayOption.perOption),
      getEnumOption('TestAnswerFeedbackDisplayOption', TestAnswerFeedbackDisplayOption.combined),
    ],
  },
  useConfigs: [useHiddenTestFeedback],
})

const FEEDBACK = genElementValueField({
  as: FieldSelect<TestAnswerFeedbackSelectOption>,
  name: 'feedback',
  label: t('elements.testAnswerFeedback.form.feedback'),
  layout: 'horizontal',
  info: t('input.tooltip.feedback'),
  defaultValue: TestAnswerFeedbackSelectOption.all,
  props: {
    options: [
      getEnumOption('TestAnswerFeedbackSelectOption', TestAnswerFeedbackSelectOption.selected),
      getEnumOption('TestAnswerFeedbackSelectOption', TestAnswerFeedbackSelectOption.all),
    ],
  },
  useConfigs: [useHiddenTestFeedback, useHiddenPerOption],
})

const OPTION_TYPE_FB = genElementValueField({
  as: FieldSelect<TestAnswerFeedbackType>,
  name: 'optionTypeFb',
  label: t('elements.testAnswerFeedback.form.optionTypeFb'),
  defaultValue: TestAnswerFeedbackType.validation,
  props: {
    options: [
      getEnumOption('TestFeedbackType', TestAnswerFeedbackType.validation),
      getEnumOption('TestFeedbackType', TestAnswerFeedbackType.info),
    ],
  },
  useConfigs: [useHiddenTestFeedback, useHiddenPerOption],
})

const GENERAL_TYPE_FB = genElementValueField({
  as: FieldSelect<TestAnswerFeedbackType>,
  name: 'generalTypeFb',
  label: t('elements.testAnswerFeedback.form.generalTypeFb'),
  defaultValue: TestAnswerFeedbackType.validation,
  props: {
    options: [
      getEnumOption('TestFeedbackType', TestAnswerFeedbackType.validation),
      getEnumOption('TestFeedbackType', TestAnswerFeedbackType.info),
    ],
  },
  useConfigs: [useHiddenTestFeedback, useHiddenGeneral],
})

const TestSingleChoiceForm: TestSingleChoiceValueFormType = ({ name }) => (
  <Padding bottom gap sides>
    <Typography styleType='hint'>{t('elements.testSingleChoice.form.randomHint')}</Typography>
    <FormField config={chainName(name, IS_RANDOM)} />
    <FormField config={chainName(name, ANSWER_FEEDBACK)} />
    <FormField config={chainName(name, DISPLAY_FEEDBACK)} />
    <FormField config={chainName(name, DISPLAY_OPTION)} />
    <FormField config={chainName(name, FEEDBACK)} />
    <FormField config={chainName(name, OPTION_TYPE_FB)} />
    <FormField config={chainName(name, GENERAL_TYPE_FB)} />
  </Padding>
)

export default React.memo(TestSingleChoiceForm) as typeof TestSingleChoiceForm
